/**
 * レイヤー素材 (SVG) の入稿エラーの文言 (docs/features/motion-graphics-mode-plan.md §5.2・§6.2)。
 * backend の `POST /api/materials/{temporary,cloud}/upload` (SVG 経路 `_handle_svg_layer_upload`) は
 * 400 `{error, error_code, details, diff_image_base64?}`・429 `RETRY_LATER` / `RATE_LIMITED` を返す。
 * コードの正本は backend/services/motion_layered/ (errors.py / parse_svg.py / sandbox.py)。
 */
import { SVG_LAYER_TOO_LARGE_MESSAGE } from '../materials/svgLayerUpload';

type Details = Record<string, unknown> | null | undefined;

const MAX_LAYERS_DEFAULT = 40;
const MAX_NAMES = 5;
const MAX_FALLBACK_CHARS = 300;
/** 差分画像 (PNG の base64) の上限。backend は長辺 512 で数百 KB 程度 */
const MAX_DIFF_IMAGE_BASE64_CHARS = 2 * 1024 * 1024;
const BASE64_RE = /^[A-Za-z0-9+/=]+$/;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** details から要素名 / 属性名を最大 5 個 (重複なし) 取り出す。issues → attributes → elements の順 */
function collectNames(details: Details): { names: string[]; total: number } {
  const all: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === 'string' && v && !all.includes(v)) all.push(v);
  };
  if (isObj(details)) {
    if (Array.isArray(details.issues)) {
      for (const issue of details.issues) {
        if (isObj(issue)) push(issue.name);
      }
    }
    if (all.length === 0) {
      if (Array.isArray(details.attributes)) details.attributes.forEach(push);
      if (Array.isArray(details.elements)) details.elements.forEach(push);
    }
  }
  return { names: all.slice(0, MAX_NAMES), total: all.length };
}

function namesSuffix(details: Details): string {
  const { names, total } = collectNames(details);
  if (names.length === 0) return '';
  const more = total > names.length ? ` ほか ${total - names.length} 件` : '';
  return `: ${names.join(', ')}${more}`;
}

const COMPLEX_MESSAGE = 'SVG が複雑すぎます。要素をまとめてください';
const BUSY_MESSAGE = '混み合っています。しばらくしてからもう一度お試しください';

/**
 * 入稿エラーのコードから利用者向けの文言を作る (純関数)。
 * 対応表に無いコードは backend の `error` (fallback) をそのまま使い、それも無ければ汎用の文言。
 */
export function layeredIntakeErrorMessage(
  code: string | null | undefined,
  details?: Details,
  fallback?: string | null,
): string {
  switch (code) {
    case 'SVG_TEXT_NOT_OUTLINED':
      return '文字をアウトライン化してから入稿してください (Illustrator: 書き出し時に『文字: アウトラインに変換』 / Figma: Outline text)';
    case 'SVG_EMBEDDED_CONTENT':
    case 'SVG_EXTERNAL_REFERENCE':
      // PNG / JPEG の埋め込み画像 (data:) はラスタのレイヤーとして受け付ける (§3.3・§3.6-5)。断るのは外部参照と それ以外の埋め込み
      return '外部の画像や SVG 以外のファイルを含む SVG は受け付けません (PNG / JPEG の埋め込み画像は受け付けます)';
    case 'SVG_EMBEDDED_IMAGE_INVALID':
      return '埋め込み画像を読み込めませんでした (PNG / JPEG のみ)';
    case 'SVG_EMBEDDED_IMAGE_TOO_LARGE':
      return '埋め込み画像が大きすぎます (1 枚 4096×4096 まで)';
    case 'SVG_ANIMATION':
      return 'SVG 自身のアニメーションは受け付けません';
    case 'SVG_TOO_MANY_LAYERS': {
      const max = isObj(details) && typeof details.max === 'number' ? details.max : MAX_LAYERS_DEFAULT;
      const count = isObj(details) && typeof details.layers === 'number' ? details.layers : null;
      return `レイヤーは ${max} 枚までです${count !== null ? ` (この SVG は ${count} 枚以上)` : ''}。グループにまとめてから書き出してください`;
    }
    case 'SVG_CSS_RESOLVE_MISMATCH':
      return 'スタイルの解決に失敗しました。書き出し設定でスタイルをインライン化してください';
    case 'SVG_SPLIT_MISMATCH':
      return 'レイヤーに分割するとデザインが崩れます。親グループの不透明度・効果・合成モードを外すか、レイヤーごとに分けてから書き出してください';
    case 'SVG_DISALLOWED_ELEMENT':
    case 'SVG_DISALLOWED_ATTRIBUTE':
    case 'SVG_DISALLOWED_VALUE':
      return `対応していない要素 / 属性があります${namesSuffix(details)}`;
    case 'SVG_ANALYSIS_TIMEOUT':
    case 'SVG_TOO_COMPLEX':
    case 'SVG_TOO_MANY_ELEMENTS_TO_PROBE':
      return COMPLEX_MESSAGE;
    case 'SVG_TOO_LARGE':
      return SVG_LAYER_TOO_LARGE_MESSAGE;
    case 'RETRY_LATER':
    case 'RATE_LIMITED':
      return BUSY_MESSAGE;
    default: {
      // 対応表に無いコードは backend の文言 (要素名を列挙するので長くなりうる) を 300 文字で切る
      const text = fallback?.trim();
      if (!text) return 'SVG を受け付けられませんでした';
      return text.length > MAX_FALLBACK_CHARS ? `${text.slice(0, MAX_FALLBACK_CHARS)}…` : text;
    }
  }
}

export type LayeredIntakeError = {
  code: string;
  message: string;
  diffImageBase64: string | null;
};

/** SVG 入稿の経路が返すコードか (SVG_* と 429 の 2 種・レイヤー素材の作成失敗) */
function isLayeredIntakeCode(code: string): boolean {
  return code.startsWith('SVG_') || code === 'RETRY_LATER' || code === 'RATE_LIMITED' || code === 'LAYER_MATERIAL_FAILED';
}

/**
 * axios のエラー (response.data) から入稿エラーを取り出す。SVG 経路のコードでなければ null
 * (呼び出し側は従来のエラー表示にする)。
 */
export function extractLayeredIntakeError(error: unknown): LayeredIntakeError | null {
  if (!isObj(error)) return null;
  const response = error.response;
  if (!isObj(response)) return null;
  const data = response.data;
  if (!isObj(data)) return null;
  const code = typeof data.error_code === 'string' ? data.error_code : '';
  if (!code || !isLayeredIntakeCode(code)) return null;
  const details = isObj(data.details) ? data.details : null;
  const fallback = typeof data.error === 'string' ? data.error : null;
  // data: URL に埋めるので base64 の文字だけ・2 MB までに限る (それ以外は出さない)
  const rawDiff = data.diff_image_base64;
  const diff = typeof rawDiff === 'string' && rawDiff
    && rawDiff.length <= MAX_DIFF_IMAGE_BASE64_CHARS && BASE64_RE.test(rawDiff)
    ? rawDiff
    : null;
  return {
    code,
    message: layeredIntakeErrorMessage(code, details, fallback),
    diffImageBase64: diff,
  };
}
